/* ============================================================
   ORION PRE BUYBOX — one-time bundle ladder for a single variant.

   The tiles arrive from Liquid with their exact totals already on them.
   This script only tracks which tile is on, mirrors it into the price
   line and the CTA, hosts the supplement facts (the DPP's nutrition
   contract: one embedded orion-nutrition node, moved into the strip's
   expander on desktop or the bottom sheet on phones) and adds the
   tile's quantity of the one variant to the cart. The free units are a
   native automatic discount, so nothing here touches price.

   Same cart contract as the other buyboxes: /cart/add.js with the
   drawer's section ids, then `orion:cart:added` for orion-cart.js to
   open and morph the drawer.
   ============================================================ */
(() => {
  class OrionPreBuybox extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === '1') return;
      this.dataset.ready = '1';

      const strings = this.querySelector('[data-o-strings]');
      this.strings = strings ? JSON.parse(strings.textContent) : {};
      this.variant = Number(this.dataset.variant);
      this.available = this.dataset.available === 'true';

      this.els = {
        tiers: [...this.querySelectorAll('[data-o-pre-tier]')],
        now: this.querySelector('[data-o-pre-now]'),
        was: this.querySelector('[data-o-pre-was]'),
        cta: this.querySelector('[data-o-cta]'),
        error: this.querySelector('[data-o-error]'),
      };

      this.els.tiers.forEach((tile) => tile.addEventListener('click', () => this.select(tile)));
      this.querySelectorAll('[data-o-pre-thumb]').forEach((btn) => btn.addEventListener('click', () => this.thumb(btn)));
      if (this.els.cta) this.els.cta.addEventListener('click', () => this.add());
      this.wireNutrition();

      const on = this.els.tiers.find((t) => t.classList.contains('is-on')) || this.els.tiers[0];
      if (on) this.select(on); else this.paint();
    }

    money(cents) { return '$' + (cents / 100).toFixed(2); }

    thumb(btn) {
      const src = btn.dataset.src;
      const main = this.querySelector('[data-o-main] img');
      if (!src || !main) return;
      this.querySelectorAll('[data-o-pre-thumb]').forEach((b) => b.classList.toggle('is-active', b === btn));
      // image_tag wrote a srcset; without clearing it the browser keeps
      // choosing from the old candidates and ignores the new src
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.src = src;
    }

    select(tile) {
      this.els.tiers.forEach((t) => {
        const on = t === tile;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-checked', String(on));
      });
      this.tier = {
        qty: Number(tile.dataset.qty) || 1,
        total: Number(tile.dataset.total) || 0,
        value: Number(tile.dataset.value) || 0,
      };
      this.paint();
    }

    paint() {
      const t = this.tier || { qty: 1, total: 0, value: 0 };
      if (this.els.now) this.els.now.textContent = this.money(t.total);
      if (this.els.was) {
        this.els.was.textContent = this.money(t.value);
        this.els.was.hidden = !(t.value > t.total);
      }
      if (this.els.cta) {
        if (!this.available) {
          this.els.cta.disabled = true;
          this.els.cta.textContent = this.strings.soldOut || 'Sold out';
        } else {
          this.els.cta.disabled = false;
          this.els.cta.textContent = `${this.strings.cta || 'Add to cart'} · ${this.money(t.total)}`;
        }
      }
      // the sticky bar mirrors the price node on this event
      document.dispatchEvent(new CustomEvent('orion:variant:changed'));
    }

    /* One nutrition node, two homes, exactly as on the packs buybox. The
       embedded orion-nutrition section is MOVED (not copied) into whichever
       home opens, so its tab listeners survive and both surfaces show the
       single editor-configured source. */
    wireNutrition() {
      const source = document.querySelector('[data-o-nutri-embed-source]');
      if (!source) return;
      const adopt = (slot) => {
        if (source.parentElement !== slot) slot.appendChild(source);
        source.hidden = false;
      };

      const btn = this.querySelector('[data-o-nutri-open]');
      const panel = this.querySelector('[data-o-nutri-panel]');
      if (btn && panel) {
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const opening = !panel.classList.contains('is-open');
          if (opening) {
            adopt(panel.querySelector('[data-o-nutri-slot]'));
            panel.hidden = false;
            // two frames so the 0fr starting state paints before it grows
            requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
          } else {
            panel.classList.remove('is-open');
          }
          btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        });
      }

      const chip = this.querySelector('[data-o-nutri-chip]');
      const dialog = this.querySelector('[data-o-nutri-dialog]');
      if (!chip || !dialog) return;
      const sheet = dialog.querySelector('.o-buy__media-sheet');
      const openDrawer = () => {
        adopt(dialog.querySelector('[data-o-nutri-dialog-slot]'));
        sheet.scrollTop = 0;
        dialog.showModal();
        dialog.focus({ preventScroll: true });
        document.documentElement.classList.add('o-drawer-lock');
        // a synchronous layout read makes the off-screen frame exist before
        // the slide; WebKit can coalesce rAF to before the first paint
        dialog.getBoundingClientRect();
        requestAnimationFrame(() => {
          dialog.getBoundingClientRect();
          dialog.classList.add('is-in');
        });
      };
      const closeDrawer = () => {
        dialog.classList.remove('is-in');
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          dialog.style.transform = '';
          dialog.style.transition = '';
          document.documentElement.classList.remove('o-drawer-lock');
          dialog.close();
        };
        dialog.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 460); // never leave a dead dialog behind
      };
      chip.hidden = false;
      chip.addEventListener('click', openDrawer);
      dialog.addEventListener('click', (e) => { if (e.target === dialog) closeDrawer(); });
      dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeDrawer(); });
      const close = dialog.querySelector('[data-o-nutri-dialog-close]');
      if (close) close.addEventListener('click', closeDrawer);
      // swipe-down to close, armed only when the sheet is scrolled to its top
      let y0 = null;
      sheet.addEventListener('touchstart', (e) => {
        y0 = sheet.scrollTop <= 0 ? e.touches[0].clientY : null;
      }, { passive: true });
      sheet.addEventListener('touchmove', (e) => {
        if (y0 === null) return;
        const dy = e.touches[0].clientY - y0;
        if (dy > 0) {
          dialog.style.transition = 'none';
          dialog.style.transform = 'translateY(' + dy * 0.85 + 'px)';
        }
      }, { passive: true });
      sheet.addEventListener('touchend', (e) => {
        if (y0 === null) return;
        const dy = e.changedTouches[0].clientY - y0;
        dialog.style.transition = '';
        if (dy > 110) closeDrawer(); else dialog.style.transform = '';
        y0 = null;
      });
    }

    async add() {
      if (!this.variant || !this.available) return;
      const qty = (this.tier && this.tier.qty) || 1;
      this.els.cta.classList.add('is-loading');
      if (this.els.error) this.els.error.hidden = true;
      try {
        const sectionIds = [...document.querySelectorAll('cart-items-component')]
          .map((el) => el.dataset.sectionId).filter(Boolean);
        const body = { items: [{ id: this.variant, quantity: qty }] };
        if (sectionIds.length) body.sections = sectionIds.join(',');
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const resp = await r.json();
        if (!r.ok) throw new Error(resp.description || this.strings.addFail);
        document.dispatchEvent(new CustomEvent('orion:cart:added', { detail: { sections: resp.sections } }));
      } catch (err) {
        if (this.els.error) {
          this.els.error.textContent = err.message || this.strings.addFail || 'Could not add to cart.';
          this.els.error.hidden = false;
        }
      } finally {
        this.els.cta.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('orion-pre-buybox')) {
    customElements.define('orion-pre-buybox', OrionPreBuybox);
  }
})();
